import { RoundedFilledButton } from '@/components/buttons/Buttons';
import LoadingBlocker from '@/components/dialog/LoadingBlocker';
import { TextBodyMedium, TextHeadlineSmall } from '@/components/text/Texts';
import ConfirmationDialogHeader from '@/modules/confirmation/components/ConfirmationDialogHeader';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { confirmDialog } from 'primereact/confirmdialog';
import { useEffect, useRef, useState } from 'react';
import BillingInfoForm, { BillingFormLayoutType, BillingInfoFormRef } from '../components/forms/BillingInfoForm';
import styles from './billing-dialog.module.css';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { BillingApi, BillingInfoDto } from '../billing.api';
import { Column, Row } from '@/components/container/Container';
import { createTypedModal, useTypedModal } from '@/common/common_utils';
import { Dialog } from 'primereact/dialog';
import NiceModal from '@ebay/nice-modal-react';
import ConfirmModal from '@/modules/people/components/ConfirmModal';

const RequestBillingInfoFormModal = createTypedModal<{}, undefined>(() => {
	const modal = useTypedModal();
	const [isLoading, setIsLoading] = useState(false);
	const form = useForm<BillingInfoDto>();

	const formRef = useRef<BillingInfoFormRef>(null);
	const [isFormDirty, setIsFormDirty] = useState(false);


	const billingInfoQ = useQuery({
		queryKey: ['RequestBillingInfoFormDialog.billingInfo'],
		queryFn: BillingApi.getBillingInfo,
		initialData: {} as BillingInfoDto
	});

	useEffect(() => {
		if (billingInfoQ.data) {
			form.reset(billingInfoQ.data);
		}
	}, [billingInfoQ.data, form]);

	const handleCancel = async () => {
		await NiceModal.show(ConfirmModal, {
			title: 'Discard changes?',
			message: 'You have unsaved changes. Are you sure you want to discard your changes?',
			acceptLabel: 'Discard Changes',
			rejectLabel: 'Cancel',
			onAccept: async () => modal.resolveRemove()
		});
	}

	const onSubmit = async (data: BillingInfoDto) => {
		setIsLoading(true);
		try {
			await BillingApi.updateBillingInfo(data);

			setIsLoading(false);
			modal.resolveRemove();
			showNotification('Billing information update request submitted. Awaiting approval from Admin/Manager.');
			await billingInfoQ.refetch();
		} catch (ex) {
			setIsLoading(false);
		}
	};

	return (
		<Dialog
			unstyled
			visible={true}
			closable={false}
			draggable={false}
			onHide={() => modal.resolveRemove()}
			maskStyle={{ backgroundColor: '#00000047' }}
			pt={{
				root: { className: 'py-6 px-4 rounded-[28px] w-[668px] tablet:!w-[calc(100vw-6rem)] tablet:!max-w-[calc(100vw-6rem)] bg-surfaceContainerHigh relative' },
				content: { className: 'p-0' },
			}}
		>
			<LoadingBlocker
				blocked={isLoading}
				pt={{ mask: { className: styles.loadingBlocker } }}
				blockTemplate="Sending request..."
				color="#0A84FF"
				thin
				full
			>
				<Column className="px-2">
					<TextHeadlineSmall className="mb-4 text-onSurface" text="Request Billing Info" />
					<TextBodyMedium className="text-onSurfaceVariant" text="To proceed with this request, please provide the necessary billing info details." />
				</Column>

				<div className="h-[1px] w-full my-6 bg-neutralVariant30" />

				<Column className="px-2">
					<BillingInfoForm
						ref={formRef}
						formName="add-billing-info"
						initialData={billingInfoQ.data}
						onSubmit={onSubmit}
						layoutType={BillingFormLayoutType.BillingChange}
						onDirtyChange={setIsFormDirty}
					/>

					<Row className="justify-end gap-2 mt-6">
						<RoundedFilledButton
							label="Cancel"
							id="cancel-request-button"
							thin
							clear
							onClick={handleCancel}
						/>

						<RoundedFilledButton
							label="Request"
							id="request-button"
							thin
							onClick={() => formRef.current?.submit()}
							disabled={!isFormDirty}
						/>
					</Row>
				</Column>
			</LoadingBlocker>
		</Dialog>
	);
});

export default RequestBillingInfoFormModal;
